/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
'use strict'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Navbar, Nav } from 'react-bootstrap'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import logo from '../../assets/fbot_menu.png'
import greenActivityIndicator from '../../assets/greenActivityIndicator.png'
import purpleActivityIndicator from '../../assets/purpleActivityIndicator.png'
import redActivityIndicator from '../../assets/redActivityIndicator.png'
import { StatusContext } from './StatusContext'
import { BeContext } from './BeContext'
import { useState, useContext, useEffect, useRef, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGear, faArrowLeft, faListUl, faCheck, faTerminal, faCircleArrowUp } from '@fortawesome/free-solid-svg-icons'
import AsyncStatusPanel from './AsyncStatusPanel'
import { Outlet } from 'react-router'
import { io } from 'socket.io-client'
import { MetadataContext, MetadataContextType } from './MetadataContext'
import { WsContext } from './WsContext'
import { useTheme } from 'styled-components'
import NavDropdown from 'react-bootstrap/NavDropdown'
import { exportBackup, importBackup, publish, authMe, logout as authLogout, getMetadata, queueAiProbe, aiRefresh, execControl, mergeAbort, mergeLoadFile, mergeGetSession, checkUpdateNotifications, rebaseSnapshot, getUsageStats, getSetting, setSetting } from '../data'
import { compareSemver, extractSemverFromFilename, extractSemverFromText } from '../utils/versioning'
import SaveDialog from './SaveDialog'
import LoadDialog from './LoadDialog'
import { useSoundManager } from './SoundContext'
import { faVolumeHigh, faVolumeXmark } from '@fortawesome/free-solid-svg-icons'

function App() {
    const navigate = useNavigate();
    const location = useLocation();
    const statusContext = useContext(StatusContext);
    const beContext = useContext(BeContext);
    const wsContext = useContext(WsContext);
    const [ showNetBusy, setShowNetBusy ] = useState(false);
    const [ toasts, setToasts ] = useState([]);
    const [ showLogWindow, setShowLogWindow ] = useState(false);
    const [ logFadingOut, setLogFadingOut ] = useState(false);
    const [ beRefresh, setBeRefresh ] = useState(true);
    const [ beSelection, setBeSelection ] = useState({});
    const [ ws, setWs ] = useState(null);
    const [ authState, setAuthState ] = useState({
        status: 'checking',
        username: '',
        role: '',
        capabilities: []
    });
    const [ metadata, setMetadata ] = useState(new MetadataContextType());
    const [ openSaveDialog, setOpenSaveDialog ] = useState(false);
    const [ openLoadDialog, setOpenLoadDialog ] = useState(false);
    const [ loadDialogPreselect, setLoadDialogPreselect ] = useState(null);
    const [ openMergeLoadDialog, setOpenMergeLoadDialog ] = useState(false);
    const [ updateNotification, setUpdateNotification ] = useState(null);
    const [openPublishDialog, setOpenPublishDialog] = useState(false);
    const [ currentMerge, setCurrentMerge ] = useState(null);
    const [ futureDbWarned, setFutureDbWarned ] = useState(false);
    const [ refresh, setRefresh ] = useState(0); // to force re-render on version change
    const theme = useTheme();
    const LOG_IDLE_HIDE_MS = 3500;
    const LOG_FADE_MS = 200;
    const hideTimerRef = useRef(null);
    const fadeTimerRef = useRef(null);
    const prevActivityTsRef = useRef(0);
    const logScrollRef = useRef(null);
    const stickToBottomRef = useRef(true);
    const manualLogOpenRef = useRef(false);
    const logWindowRef = useRef(null);
    const logIconRef = useRef(null);
    const startupFileOpSetRef = useRef(false);
    const [ asyncOps, setAsyncOps ] = useState([]);
    const [ showAsyncPanel, setShowAsyncPanel ] = useState(false);
    const [ unseenAsyncCount, setUnseenAsyncCount ] = useState(0);
    const [ usageStats, setUsageStats ] = useState(null);
    const [ costOffset, setCostOffset ] = useState(0);
    const soundManager = useSoundManager();
    const { muted: soundsMuted, playSound, reloadSettings, setMutedPersisted } = soundManager;
    const ASYNC_AUTO_CLOSE_MS = 4000;
    const asyncPanelOpenRef = useRef(false);
    const asyncPanelRef = useRef(null);
    const asyncIconRef = useRef(null);
    // The panel opened itself for the current run of operations, so it may
    // close itself again; a panel the user opened stays until they close it
    const asyncAutoOpenedRef = useRef(false);
    const asyncCloseTimerRef = useRef(null);
    // Operations that have started and not yet reported a result
    const asyncRunningRef = useRef(new Set());
    // The user dismissed the panel mid run, so it must not spring back
    const asyncUserClosedRef = useRef(false);
    const socketRef = useRef(null);
    const hadWsConnectionRef = useRef(null);
    const execBusyRef = useRef(null);
    const completedProbeIdsRef = useRef(new Set());
    const usageCostRef = useRef(null);
    const COST_OFFSET_SETTING_KEY = '/display/costOffset';
    const COST_OFFSET_SETTING_DESCRIPTION = 'AI cost offset used for incremental display';

    const handleFingerprintsNavClick = (e) => {
        e.preventDefault();
        if (location.pathname === '/fingerprints') {
            navigate('/fingerprints', {
                replace: true,
                state: { forceResetTs: Date.now() },
            });
            return;
        }
        navigate('/fingerprints');
    };

    const clearLogHideTimers = () => {
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
        }
        if (fadeTimerRef.current) {
            clearTimeout(fadeTimerRef.current);
        }
    };

    const clearAsyncCloseTimer = () => {
        if (asyncCloseTimerRef.current) {
            clearTimeout(asyncCloseTimerRef.current);
            asyncCloseTimerRef.current = null;
        }
    };

    /*
     * The one way the panel goes away, so the memory of having opened it can
     * never be left set behind a closed panel. Anything the user drives passes
     * through here and takes ownership away from the automatic behaviour.
     */
    const closeAsyncPanel = () => {
        clearAsyncCloseTimer();
        setShowAsyncPanel(false);
        asyncPanelOpenRef.current = false;
        asyncAutoOpenedRef.current = false;
        if (asyncRunningRef.current.size > 0) {
            asyncUserClosedRef.current = true;
        }
    };

    const handleStatusIndicatorClick = () => {
        window.dispatchEvent(new CustomEvent('fbot:present-advance'));
    };

    function parseDirFile(name) {
        const clean = name.replace(/^fbot\//, '');
        const parts = clean.split('/').filter(Boolean);
        const file = parts.pop() || '';
        const dir = parts.join('/') || '';
        return { dir, file };
    }
    statusContext.setShowNetBusy = setShowNetBusy;
    statusContext.toasts = toasts;
    statusContext.setToasts = setToasts;
    statusContext.asyncOps = asyncOps;
    statusContext.setAsyncOps = setAsyncOps;
    statusContext.setUnseenAsyncCount = setUnseenAsyncCount;
    /*
     * Both of these run from a socket callback, so they read refs rather than
     * state: the handler that calls them was captured when the socket was
     * built and would see a stale showAsyncPanel forever.
     */
    statusContext.onAsyncOpNew = (id) => {
        asyncRunningRef.current.add(id);
        // A new operation cancels a close that was pending for the last one
        clearAsyncCloseTimer();
        if (asyncPanelOpenRef.current) {
            return;
        }
        if (asyncUserClosedRef.current) {
            // Dismissed during this run, so report it on the badge instead of
            // shoving the panel back in front of them
            setUnseenAsyncCount(n => n + 1);
            return;
        }
        setShowAsyncPanel(true);
        asyncPanelOpenRef.current = true;
        asyncAutoOpenedRef.current = true;
        setUnseenAsyncCount(0);
    };
    statusContext.onAsyncOpEnd = (id, ok) => {
        asyncRunningRef.current.delete(id);
        if (asyncRunningRef.current.size === 0) {
            // The run is over, so the next one starts with a clean slate
            asyncUserClosedRef.current = false;
        }
        if (!ok) {
            /*
             * A failure stays on screen to be read, and the panel stops being
             * ours, so a later success cannot schedule a close over the top of
             * an error the user has not seen yet.
             */
            clearAsyncCloseTimer();
            asyncAutoOpenedRef.current = false;
            return;
        }
        if (!asyncAutoOpenedRef.current || asyncRunningRef.current.size > 0) {
            return;
        }
        clearAsyncCloseTimer();
        asyncCloseTimerRef.current = setTimeout(() => {
            asyncCloseTimerRef.current = null;
            // Checked again on the way out: the user may have taken the panel
            // over, or another operation started, while this was pending
            if (asyncAutoOpenedRef.current && asyncRunningRef.current.size === 0) {
                closeAsyncPanel();
            }
        }, ASYNC_AUTO_CLOSE_MS);
    };
    beContext.beSelection = beSelection;
    beContext.setBeSelection = setBeSelection;
    beContext.setBeRefresh = setBeRefresh;
    wsContext.ws = ws;
    wsContext.setWs = setWs;

    const beContextValue = useMemo(() => ({
        ...beContext,
        beSelection,
        setBeSelection,
        setBeRefresh,
        count: () => Object.keys(beSelection).length,
        selPut: (...args) => beContext.selPut(...args),
        selRefresh: (...args) => beContext.selRefresh(...args),
        selSetOne: (...args) => beContext.selSetOne(...args),
    }), [beContext, beSelection, setBeSelection, setBeRefresh]);

    const wsContextValue = useMemo(() => ({ ws, setWs }), [ws]);

    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
        statusContext.netBusy();
        try {
            const response = await originalFetch(...args);
            return response;
        } catch (error) {
            throw error;
        } finally {
            statusContext.netIdle();
        }
    }

    useEffect(() => {
        async function work() {
            if (!beRefresh) {
                return;
            }
            try {
                await beContext.selRefresh();
                setBeRefresh(false);
            } catch (e) {
                console.error(e);
                statusContext.postError(e.message, 5000);
            }
        }
        work();
    }, [beRefresh]); // eslint-disable-line react-hooks/exhaustive-deps
    const newestToastTs = toasts.length > 0 ? (toasts[toasts.length - 1].ts || 0) : 0;

    useEffect(() => {
        if (!newestToastTs || newestToastTs === prevActivityTsRef.current) {
            return;
        }
        prevActivityTsRef.current = newestToastTs;

        clearLogHideTimers();

        setShowLogWindow(true);
        setLogFadingOut(false);

        if (manualLogOpenRef.current) {
            return;
        }

        hideTimerRef.current = setTimeout(() => {
            if (manualLogOpenRef.current) {
                return;
            }
            setLogFadingOut(true);
            fadeTimerRef.current = setTimeout(() => {
                if (manualLogOpenRef.current) {
                    return;
                }
                setShowLogWindow(false);
                setLogFadingOut(false);
            }, LOG_FADE_MS);
        }, LOG_IDLE_HIDE_MS);

        return () => {
            clearLogHideTimers();
        };
    }, [newestToastTs]);

    useEffect(() => {
        async function checkAuth() {
            try {
                const me = await authMe();
                setAuthState({
                    status: 'authenticated',
                    username: me.username,
                    role: me.role,
                    capabilities: me.capabilities || []
                });
            } catch (_e) {
                setAuthState({ status: 'unauthenticated', username: '', role: '', capabilities: [] });
                const nextPath = `${location.pathname || '/'}${location.search || ''}${location.hash || ''}`;
                navigate(`/login?next=${encodeURIComponent(nextPath)}`, { replace: true });
            }
        }
        checkAuth();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (authState.status !== 'authenticated') {
            return;
        }
        reloadSettings().catch(() => {});
    }, [authState.status, reloadSettings]);

    useEffect(() => {
        if (authState.status !== 'authenticated') {
            hadWsConnectionRef.current = null;
            execBusyRef.current = null;
            completedProbeIdsRef.current = new Set();
            usageCostRef.current = null;
            if (socketRef.current) {
                socketRef.current.close();
                socketRef.current = null;
            }
            return;
        }
        if (socketRef.current) {
            socketRef.current.close();
            socketRef.current = null;
        }
        const socket = io();
        socketRef.current = socket;
        const handlers = {
            'connect': () => {
                console.log('WebSocket connected');
                if (hadWsConnectionRef.current === false) {
                    void playSound('websocket_up');
                }
                hadWsConnectionRef.current = true;
                setWs(socket);
                global.myWebsocket = socket;
                global.ws_waiters.forEach((waiter) => waiter());
                global.ws_waiters = [];
            },
            'disconnect': (reason, details) => {
                if (socketRef.current === socket) {
                    socketRef.current = null;
                }
                if (hadWsConnectionRef.current === true) {
                    void playSound('websocket_down');
                }
                hadWsConnectionRef.current = false;
                setWs(null);
                global.myWebsocket = null;
                console.log('WebSocket disconnected', { reason, details });
//                socket.off('connect', handlers.connect);
//                socket.off('disconnect', handlers.disconnect);
//                socket.off('status', handlers.status);
//                socket.off('error', handlers.error);
//                socket.off('connect_error', handlers.connect_error);
            },
            'status': (data) => {
                console.log('Received probe detail:', data);
            },
            'error': (error) => {
                console.error('WebSocket error:', error);
            },
            'connect_error': (error) => {
                console.error('WebSocket connection error:', {
                    message: error?.message,
                    description: error?.description,
                    context: error?.context,
                    type: error?.type,
                    data: error?.data,
                });
            },
            'log': (data) => {
                // App shell should only consume explicitly tagged events.
                let level = 'info';
                let message = '';

                if (typeof data === 'object' && data !== null) {
                    if (String(data.target || '') !== 'app-shell') {
                        return;
                    }
                    level = String(data.level || 'info').toLowerCase();
                    message = String(data.message || data.msg || JSON.stringify(data));
                } else {
                    return;
                }

                if (!message) {
                    return;
                }
                if (level === 'error' || level === 'err') {
                    statusContext.postError(message);
                } else {
                    statusContext.postInfo(message);
                }
            },
            'refreshMetadata': () => {
                // Backend has updated metadata, refresh client-side state
                async function fetchMetadata() {
                    try {
                        const data = await getMetadata();
                        setMetadata(prev => ({ ...prev, ...data }));
                    } catch (error) {
                        console.error('Error fetching metadata:', error);
                    }
                }
                fetchMetadata();
            },
            // Opening, badging and closing all hang off receiveAsyncOp, so a
            // locally raised operation behaves the same as one from the server
            'asyncOp': (data) => {
                statusContext.receiveAsyncOp(data);
            },
            'pmStats': (data) => {
                const runningCount = Array.isArray(data?.runningProbes) ? data.runningProbes.length : 0;
                const execState = String(data?.execState || 'stopped');
                const isBusy = runningCount > 0 || execState === 'running' || execState === 'run_one';
                if (execBusyRef.current === null) {
                    if (isBusy) {
                        void playSound('executor_busy');
                    }
                    execBusyRef.current = isBusy;
                } else if (execBusyRef.current !== isBusy) {
                    void playSound(isBusy ? 'executor_busy' : 'executor_idle');
                    execBusyRef.current = isBusy;
                }
            },
            'probeLifecycle': (data) => {
                const probeId = Number(data?.id || 0);
                const finished = probeId > 0 && data?.running === false && data?.pending === false;
                if (!finished || completedProbeIdsRef.current.has(probeId)) {
                    return;
                }
                completedProbeIdsRef.current.add(probeId);
                void playSound(data?.passed ? 'task_pass' : 'task_fail');
            },
            'updateNotification': (data) => {
                setUpdateNotification(data);
            },
            'usageStats': (data) => {
                const totalCost = Number(data?.total_cost || 0);
                if (usageCostRef.current !== null && totalCost > usageCostRef.current + 0.000001) {
                    void playSound('cost_up');
                }
                usageCostRef.current = totalCost;
                setUsageStats(data);
            },
        };
        socket.on('connect', handlers.connect);
        socket.on('disconnect', handlers.disconnect);
        socket.on('status', handlers.status);
        socket.on('error', handlers.error);
        socket.on('connect_error', handlers.connect_error);
        socket.on('log', handlers.log);
        socket.on('refreshMetadata', handlers.refreshMetadata);
        socket.on('asyncOp', handlers.asyncOp);
        socket.on('pmStats', handlers.pmStats);
        socket.on('probeLifecycle', handlers.probeLifecycle);
        socket.on('updateNotification', handlers.updateNotification);
        socket.on('usageStats', handlers.usageStats);
        return () => {
            socket.off('connect', handlers.connect);
            socket.off('disconnect', handlers.disconnect);
            socket.off('status', handlers.status);
            socket.off('error', handlers.error);
            socket.off('connect_error', handlers.connect_error);
            socket.off('log', handlers.log);
            socket.off('refreshMetadata', handlers.refreshMetadata);
            socket.off('asyncOp', handlers.asyncOp);
            socket.off('pmStats', handlers.pmStats);
            socket.off('probeLifecycle', handlers.probeLifecycle);
            socket.off('updateNotification', handlers.updateNotification);
            socket.off('usageStats', handlers.usageStats);
            if (socketRef.current === socket) {
                socketRef.current = null;
            }
            socket.close();
        };
    }, [authState.status, playSound]);

    useEffect(() => {
        if (authState.status !== 'authenticated') {
            return;
        }
        async function loadCostOffset() {
            try {
                const offsetRaw = await getSetting(COST_OFFSET_SETTING_KEY).catch(() => '0');
                const parsedOffset = Number(offsetRaw);
                setCostOffset(Number.isFinite(parsedOffset) ? parsedOffset : 0);
            } catch (error) {
                console.error('Error loading cost offset setting:', error);
                setCostOffset(0);
            }
        }
        loadCostOffset();
    }, [authState.status]);

    useEffect(() => {
        if (authState.status !== 'authenticated') {
            return;
        }
        async function fetchMetadata() {
            try {
                const data = await getMetadata();
                setMetadata({
                    ...data,
                    refresh: () => { setRefresh(v => v + 1); }
                });
                if (!startupFileOpSetRef.current) {
                    try {
                        const lastFilename = String(data?.lastFileOp?.filename || '').trim();
                        const lastCloud = String(data?.lastFileOp?.cloud || 'false').trim();
                        if (lastFilename) {
                            setMetadata(prev => ({
                                ...prev,
                                lastFileOp: {
                                    filename: lastFilename,
                                    cloud: lastCloud
                                }
                            }));
                        }
                    } catch (e) {
                        // Ignore
                    }
                    startupFileOpSetRef.current = true;
                }
                const parsedSchemaVer = Number.parseInt(data?.schemaVersion, 10);
                const parsedSupportedSchemaVer = Number.parseInt(data?.supportedSchemaVersion, 10);
                if (!Number.isNaN(parsedSchemaVer) && !Number.isNaN(parsedSupportedSchemaVer) && parsedSchemaVer > parsedSupportedSchemaVer && !futureDbWarned) {
                    statusContext.postError('Database version is from a future release and may appear corrupted in this version.');
                    setFutureDbWarned(true);
                }
            } catch (error) {
                console.error('Error fetching metadata:', error);
                setMetadata(new MetadataContextType());
            }
        }
        fetchMetadata();
    }, [ authState.status, refresh, futureDbWarned, statusContext ]);

    const visibleToasts = [...toasts].sort((a, b) => {
        const aIsUnackedError = a.type === 'error' && !a.acked ? 1 : 0;
        const bIsUnackedError = b.type === 'error' && !b.acked ? 1 : 0;
        if (aIsUnackedError !== bIsUnackedError) {
            // Keep unacknowledged errors closer to the bottom of the list.
            return aIsUnackedError - bIsUnackedError;
        }
        return (a.ts || 0) - (b.ts || 0);
    });
    const hasUnacknowledgedErrors = toasts.some((toast) => toast.type === 'error' && !toast.acked);
    const logIconColor = hasUnacknowledgedErrors ? '#ef4444' : '#22c55e';
    const totalCost = Number(usageStats?.total_cost || 0);
    const incrementalCost = Math.max(0, totalCost - costOffset);

    async function handleClearIncrementalCost() {
        const shouldClear = window.confirm('Clear incremental cost and restart from current total cost?');
        if (!shouldClear) {
            return;
        }
        const nextOffset = Number.isFinite(totalCost) ? totalCost : 0;
        try {
            await setSetting(COST_OFFSET_SETTING_KEY, String(nextOffset), COST_OFFSET_SETTING_DESCRIPTION);
            setCostOffset(nextOffset);
            statusContext.postInfo('Incremental AI cost cleared.');
        } catch (error) {
            console.error('Error saving cost offset setting:', error);
            statusContext.postError('Failed to clear incremental AI cost.');
        }
    }

    const newestVisibleToastTs = visibleToasts.length > 0
        ? (visibleToasts[visibleToasts.length - 1].ts || 0)
        : 0;

    useEffect(() => {
        const el = logScrollRef.current;
        if (!el) {
            return;
        }
        if (stickToBottomRef.current) {
            el.scrollTop = el.scrollHeight;
        }
    }, [newestVisibleToastTs, showLogWindow]);

    useEffect(() => {
        if (!showLogWindow) {
            return;
        }
        const onDocMouseDown = (event) => {
            const target = event.target;
            if (logWindowRef.current && logWindowRef.current.contains(target)) {
                return;
            }
            if (logIconRef.current && logIconRef.current.contains(target)) {
                return;
            }
            manualLogOpenRef.current = false;
            setShowLogWindow(false);
            setLogFadingOut(false);
            clearLogHideTimers();
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
        };
    }, [showLogWindow]);

    // A scheduled close must not outlive the app that scheduled it
    useEffect(() => clearAsyncCloseTimer, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!showAsyncPanel) {
            return;
        }
        const onDocMouseDown = (event) => {
            const target = event.target;
            if (asyncPanelRef.current && asyncPanelRef.current.contains(target)) {
                return;
            }
            if (asyncIconRef.current && asyncIconRef.current.contains(target)) {
                return;
            }
            closeAsyncPanel();
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
        };
    }, [showAsyncPanel]);

    useEffect(() => {
        if (authState.status !== 'authenticated') {
            setCurrentMerge(null);
            return;
        }
        mergeGetSession().then(res => {
            if (res.session) {
                setCurrentMerge({ name: res.session.source_name, ...(res.session.metadata || {}) });
            } else {
                setCurrentMerge(null);
            }
        }).catch(() => setCurrentMerge(null));
    }, [authState.status, location.pathname]);

    // --- Update notification helpers ---
    function parseFpSerial(filename) {
        const base = (filename || '').split('/').pop() || '';
        const m = base.match(/^ser_(\d+)\.fbot$/i);
        return m ? parseInt(m[1], 10) : null;
    }

    function isDevLibraryFile(filename) {
        return (filename || '').includes('library/dev');
    }

    function getOsReleasePathForNotification() {
        if (metadata.deploymentType === 'linux-prod' || metadata.deploymentType === 'web') {
            return 'releases/linux-prod';
        }
        if (typeof navigator === 'undefined') return '';
        const nav = navigator;
        const platform = ((nav.userAgentData?.platform || navigator.platform || '')).toLowerCase();
        if (platform.includes('mac')) return 'releases/darwin';
        if (platform.includes('win')) return 'releases/win32';
        return '';
    }

    const showFingerprintUpdate = (() => {
        if (!updateNotification?.fingerprints?.available) return false;
        const loadedFile = metadata.lastFileOp?.filename || '';
        if (!isDevLibraryFile(loadedFile)) return false;
        const loadedSerial = parseFpSerial(loadedFile);
        if (loadedSerial === null) return false;
        return updateNotification.fingerprints.latestSerial > loadedSerial;
    })();

    const showFirmwareUpdate = (() => {
        if (!updateNotification?.firmware?.updates?.length) return false;
        const osPath = getOsReleasePathForNotification();
        if (!osPath) return false;
        const current = extractSemverFromText(metadata.appVersion || '');
        if (!current) return false;
        const best = updateNotification.firmware.updates
            .filter(u => (u.name || '').toLowerCase().includes(osPath))
            .map(u => extractSemverFromFilename(u.name))
            .filter(Boolean)
            .reduce((a, b) => (compareSemver(b, a) > 0 ? b : a), '');
        return Boolean(best) && compareSemver(best, current) > 0;
    })();

    useEffect(() => {
        if (authState.status !== 'authenticated') return;
        if (!metadata.appVersion || metadata.appVersion === 'unknown') return;
        checkUpdateNotifications().then(setUpdateNotification).catch(() => {});
    }, [authState.status, metadata.appVersion, metadata.lastFileOp?.filename]);

    useEffect(() => {
        if (authState.status !== 'authenticated') return;
        getUsageStats().then((stats) => {
            usageCostRef.current = Number(stats?.total_cost || 0);
            setUsageStats(stats);
        }).catch(() => {});
    }, [authState.status]);

    const parsedSchemaVersion = Number.parseInt(metadata.schemaVersion, 10);
    const parsedSupportedSchemaVersion = Number.parseInt(metadata.supportedSchemaVersion, 10);
    const isFutureDbVersion = !Number.isNaN(parsedSchemaVersion) && !Number.isNaN(parsedSupportedSchemaVersion) && parsedSchemaVersion > parsedSupportedSchemaVersion;
    if (authState.status !== 'authenticated' || metadata.appVersion === 'unknown') {
        // block component mounting and state setup until we have metadata
        return <div>Loading...</div>;
    }

    const runCleanup = async () => {
        try {
            await aiRefresh();
            const probeId = await queueAiProbe('data_cleanup_plan');
            await execControl(`run ${probeId}`);
            statusContext.postInfo(`Started cleanup as probe ${probeId}`);
            navigate('/probe/exec');
        } catch (e) {
            statusContext.postError(`Failed to run cleanup: ${e.message || e}`);
        }
    };


    const handleLogScroll = () => {
        const el = logScrollRef.current;
        if (!el) {
            return;
        }
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        stickToBottomRef.current = distanceFromBottom <= 16;
    };

    const handlePublishFromMenu = async (fileName, cloud, metadataOverride) => {
        try {
            await publish(fileName, cloud, metadataOverride);
            statusContext.postInfo(`Published ${fileName} ${cloud ? 'to cloud' : 'locally'}`);
            setOpenPublishDialog(false);
        } catch (e) {
            statusContext.postError(`Publish failed: ${e.message || e}`);
            throw e;
        }
    };

    const handleClearMerge = async () => {
        try {
            await mergeAbort();
            setCurrentMerge(null);
            window.dispatchEvent(new CustomEvent('fbot:mergeSessionChanged'));
            statusContext.postInfo('Merge cleared');
        } catch (e) {
            statusContext.postError(`Failed to clear merge: ${e.message || e}`);
        }
    };


    return (
    <div className="App" style={{ backgroundColor: theme.colors.background, color: theme.colors.text }}>
        <style>{`@keyframes shimmer { to { background-position: 200% center; } }`}</style>
        {showAsyncPanel && (
            <AsyncStatusPanel
                panelRef={asyncPanelRef}
                onClose={closeAsyncPanel}
            />
        )}
        <div
            ref={logWindowRef}
            aria-live="polite"
            aria-atomic="true"
            style={{
                position: 'fixed',
                top: '56px',
                right: '12px',
                width: 'min(680px, calc(100vw - 24px))',
                maxHeight: '50vh',
                zIndex: 1100,
                display: showLogWindow ? 'block' : 'none',
                opacity: logFadingOut ? 0 : 1,
                transition: `opacity ${LOG_FADE_MS}ms ease-out`,
                overflow: 'hidden',
                border: `1px solid ${theme.colors.textMuted}`,
                borderRadius: '8px',
                backgroundColor: theme.colors.toastBg,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)'
            }}
        >
            <div
                ref={logScrollRef}
                onScroll={handleLogScroll}
                style={{ maxHeight: '50vh', overflowY: 'auto', padding: '6px 0' }}
            >
                <table className="table table-sm mb-0" style={{ color: theme.colors.toastText }}>
                    <tbody>
                        {visibleToasts.length === 0 ? (
                            <tr>
                                <td colSpan={3} style={{ color: theme.colors.textMuted, fontStyle: 'italic' }}>
                                    No log issues.
                                </td>
                            </tr>
                        ) : null}
                        {visibleToasts.map((toast) => (
                            <tr key={toast.id ?? `${toast.ts}:${toast.msg}`}>
                                <td style={{ color: toast.type === 'error' ? '#dc3545' : '#0d6efd' }}>
                                    {toast.type === 'error'
                                        ? (toast.acked ? '[err ack]' : '[err]')
                                        : '[info]'}
                                </td>
                                <td style={{ color: theme.colors.ctext, fontFamily: 'monospace' }}>{toast.msg}</td>
                                <td className="text-end">
                                    {toast.type === 'error' && !toast.acked ? (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-success"
                                            onClick={() => {
                                                if (toast.id != null && statusContext.ackMsgById) {
                                                    statusContext.ackMsgById(toast.id);
                                                }
                                            }}
                                            aria-label="Acknowledge error"
                                            style={{ padding: '0 0.35rem', lineHeight: 1 }}
                                        >
                                            <FontAwesomeIcon icon={faCheck} />
                                        </button>
                                    ) : null}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
        <Container fluid className="p-0 m-0 overflow-hidden">
            <BeContext.Provider value={beContextValue}>
            <WsContext.Provider value={wsContextValue}>
                <StatusContext.Provider value={statusContext}>
                        <MetadataContext.Provider value={metadata}>
            <Row style={{ height: '48px' }}>
                <Col style={{ }}>
                    <Navbar expand="lg" bg="dark" variant="dark" style={{ "--bs-navbar-padding-y": '0px' }}>
                        <Container fluid>
                            <Navbar.Brand as={Link} to="/">
                                <div
                                    className="d-flex justify-content-center align-items-center overflow-hidden"
                                    style={{ width: '48px', height: '40px' }}
                                >
                                    <img src={logo} alt="Icon" />
                                </div>
                            </Navbar.Brand>
                            <Nav className="me-auto">
                                <Nav.Link onClick={() => navigate(-1)}>
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </Nav.Link>
                                <Nav.Link as={Link} to="/probe/exec">Executor</Nav.Link>
                                <Nav.Link as={Link} to="/dashboard/exec-status">E2</Nav.Link>
                                <Nav.Link as={Link} to="/geo">Geo</Nav.Link>
                                <NavDropdown title="File" id="file-nav-dropdown" className="navbar-dark-dropdown">
                                    <NavDropdown.Item onClick={() => setOpenLoadDialog(true)}>Load...</NavDropdown.Item>
                                    <NavDropdown.Item onClick={() => setOpenSaveDialog(true)}>Save...</NavDropdown.Item>
                                    <NavDropdown.Divider />
                                    <NavDropdown.Item
                                        as={Link}
                                        to="/data/show-changes"
                                        title="Show changes since last file load and submit to queue."
                                    >
                                        Show Changes / Submit
                                    </NavDropdown.Item>
                                    <NavDropdown.Item
                                        title="Reset the diff baseline to the current database state."
                                        onClick={async () => {
                                            try {
                                                await rebaseSnapshot();
                                                statusContext.postInfo('Rebase complete — diff baseline reset to current database.');
                                            } catch (e) {
                                                statusContext.postError(`Rebase failed: ${e?.message || e}`);
                                            }
                                        }}
                                    >
                                        Rebase
                                    </NavDropdown.Item>
                                    <NavDropdown.Divider />
                                    <NavDropdown.Item
                                        onClick={async () => {
                                            try {
                                                void playSound('logout', { force: true });
                                                await authLogout();
                                            } finally {
                                                setWs(null);
                                                global.myWebsocket = null;
                                                navigate('/login', { replace: true });
                                            }
                                        }}
                                    >
                                        Logout
                                    </NavDropdown.Item>
                                </NavDropdown>
                                <NavDropdown title="Edit" id="edit-nav-dropdown" className="navbar-dark-dropdown">
                                    <NavDropdown.Item as={Link} to="/data/ws">Working Set</NavDropdown.Item>
                                    <NavDropdown.Item as={Link} to="/data/pending-deletes">Pending Deletes</NavDropdown.Item>
                                    <NavDropdown.Divider />
                                    <NavDropdown.Item onClick={runCleanup}>Run Cleanup</NavDropdown.Item>
                                    <NavDropdown.Item as={Link} to="/data/merge-layers">Merge Layers</NavDropdown.Item>
                                </NavDropdown>
                                <NavDropdown title="Tools" id="ai-nav-dropdown" className="navbar-dark-dropdown">
                                    <NavDropdown.Header>Merge Ops</NavDropdown.Header>
                                    <NavDropdown.Item as={Link} to="/data/merge-op">View Merge</NavDropdown.Item>
                                    <NavDropdown.Item onClick={() => setOpenMergeLoadDialog(true)}>Load Merge...</NavDropdown.Item>
                                    <NavDropdown.Item as={Link} to="/data/merge-db">Merge Queue</NavDropdown.Item>
                                    <NavDropdown.Item onClick={handleClearMerge} disabled={!currentMerge}>Clear Merge</NavDropdown.Item>
                                    <NavDropdown.Divider />
                                    <NavDropdown.Header>Publish</NavDropdown.Header>
                                    <NavDropdown.Item
                                        onClick={() => setOpenPublishDialog(true)}
                                        title="Publish a trimmed database package for CI/dbfw distribution."
                                    >
                                        Publish
                                    </NavDropdown.Item>
                                    <NavDropdown.Item
                                        as={Link}
                                        to="/data/threatstop"
                                        title="Export and publish ThreatStop artifacts from the working set."
                                    >
                                        ThreatStop
                                    </NavDropdown.Item>
                                    <NavDropdown.Divider />
                                    <NavDropdown.Header>Developer Tools</NavDropdown.Header>
                                    <NavDropdown.Item as={Link} to="/dev" style={{ color: '#5eead4', fontWeight: 600 }}>
                                        Dev
                                    </NavDropdown.Item>
                                    <NavDropdown.Item as={Link} to="/ops" style={{ color: '#c4b5fd', fontWeight: 600 }}>
                                        Ops
                                    </NavDropdown.Item>
                                    <NavDropdown.Divider />
                                    <NavDropdown.Item as={Link} to="/data/progress-refresh">
                                        Manage Progress Statistics
                                    </NavDropdown.Item>
                                    <NavDropdown.Divider />
                                    <NavDropdown.Header>AI Tools</NavDropdown.Header>
                                    <NavDropdown.Item as={Link} to="/ai/op-selector">
                                        Op Menu
                                    </NavDropdown.Item>
                                    <NavDropdown.Item href="/ai/ops">
                                        Op Library
                                    </NavDropdown.Item>
                                    <NavDropdown.Item as={Link} to="/ai/opg">
                                        Op Graph
                                    </NavDropdown.Item>
                                    <NavDropdown.Item as={Link} to="/ai/connectors">
                                        Connectors
                                    </NavDropdown.Item>
                                    <NavDropdown.Item as={Link} to="/ai/leads">
                                        Leads
                                    </NavDropdown.Item>
                                </NavDropdown>
                                <Nav.Link as={Link} to="/fingerprints" onClick={handleFingerprintsNavClick}>Fingerprints</Nav.Link>
                                <Nav.Link as={Link} to="/categories">Categories</Nav.Link>
                                <NavDropdown title="Probes" id="probes-nav-dropdown" className="navbar-dark-dropdown">
                                    <NavDropdown.Item as={Link} to="/probe">All</NavDropdown.Item>
                                    <NavDropdown.Item as={Link} to="/probe/roots">Probe Roots</NavDropdown.Item>
                                </NavDropdown>
                                <NavDropdown title="Dashboards" id="dash-nav-dropdown" className="navbar-dark-dropdown">
                                    <NavDropdown.Item as={Link} to="/dashboard/summary">AI Risk Routes</NavDropdown.Item>
                                    <NavDropdown.Item as={Link} to="/dashboard/overview">Overview</NavDropdown.Item>
                                    <NavDropdown.Item as={Link} to="/dashboard/scan-results">Scan Results</NavDropdown.Item>
                                    <NavDropdown.Divider />
                                    <NavDropdown.Item as={Link} to="/dashboard/progress">Progress</NavDropdown.Item>
                                </NavDropdown>
                            </Nav>
                            <div
                                ref={asyncIconRef}
                                className="float-end me-2"
                                onClick={() => {
                                    if (showAsyncPanel) {
                                        closeAsyncPanel();
                                        return;
                                    }
                                    // Opened by hand, so it is the user's now:
                                    // no pending close survives, and none is
                                    // scheduled when the work finishes
                                    clearAsyncCloseTimer();
                                    setShowAsyncPanel(true);
                                    asyncPanelOpenRef.current = true;
                                    asyncAutoOpenedRef.current = false;
                                    asyncUserClosedRef.current = false;
                                    setUnseenAsyncCount(0);
                                }}
                                style={{ cursor: 'pointer', color: unseenAsyncCount > 0 ? '#f0a500' : '#6c757d', position: 'relative' }}
                                title="Async operations"
                            >
                                <FontAwesomeIcon icon={faTerminal} />
                                {unseenAsyncCount > 0 && (
                                    <span style={{
                                        position: 'absolute', top: -6, right: -8,
                                        background: '#f0a500', color: '#000', borderRadius: '50%',
                                        fontSize: '0.6rem', minWidth: 16, height: 16, lineHeight: '16px',
                                        textAlign: 'center', padding: '0 2px', fontWeight: 700,
                                    }}>
                                        {unseenAsyncCount > 99 ? '99+' : unseenAsyncCount}
                                    </span>
                                )}
                            </div>
                            <div
                                ref={logIconRef}
                                className="float-end me-2"
                                onClick={() => {
                                    if (showLogWindow) {
                                        manualLogOpenRef.current = false;
                                        setShowLogWindow(false);
                                        setLogFadingOut(false);
                                        clearLogHideTimers();
                                    } else {
                                        manualLogOpenRef.current = true;
                                        setShowLogWindow(true);
                                        setLogFadingOut(false);
                                        stickToBottomRef.current = true;
                                        clearLogHideTimers();
                                    }
                                }}
                                style={{ cursor: 'pointer', color: logIconColor }}
                                title="Show log issues"
                            >
                                <FontAwesomeIcon icon={faListUl} />
                            </div>
                            {showNetBusy ? (
                                <div className="text-light float-end" onClick={handleStatusIndicatorClick} style={{ cursor: 'pointer' }}>
                                    <img src={purpleActivityIndicator} alt="Icon" />
                                </div>
                            ) : ws ? (
                                <div className="text-light float-end" onClick={handleStatusIndicatorClick} style={{ cursor: 'pointer' }}>
                                    <img src={greenActivityIndicator} alt="Icon" />
                                </div>
                            ) : (
                                <div className="text-light float-end" onClick={handleStatusIndicatorClick} style={{ cursor: 'pointer' }}>
                                    <img src={redActivityIndicator} alt="Icon" />
                                </div>
                            )}
                            <div className="float-end">
                                <Nav.Link as={Link} to="/settings">
                                   <FontAwesomeIcon icon={faGear} className="me-2" />
                                </Nav.Link>
                            </div>
                        </Container>
                    </Navbar>
                </Col>
            </Row>
            <Row style={{ height: 'calc(100vh - 48px - 20px)' }} className='overflow-auto'>
                <Outlet />
            </Row>
            {(() => {
                const sep = `1px solid ${theme.colors.textMuted}`;
                const base = { flexShrink: 0, padding: '0 8px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' };
                const shimmerDev = {
                    fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em',
                    background: 'linear-gradient(90deg, #2dd4bf 0%, #818cf8 40%, #2dd4bf 80%)',
                    backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text', animation: 'shimmer 2.4s linear infinite',
                };
                const shimmerOps = {
                    ...shimmerDev,
                    background: 'linear-gradient(90deg, #a78bfa 0%, #e879f9 40%, #a78bfa 80%)',
                };
                const shimmerAdmin = {
                    ...shimmerDev,
                    background: 'linear-gradient(90deg, #f59e0b 0%, #f43f5e 40%, #f59e0b 80%)',
                };
                return (
                <div className="bg-dark text-light" style={{
                    display: 'flex', height: '20px', overflow: 'hidden', alignItems: 'center',
                    fontSize: '0.8rem', flexShrink: 0, borderTop: sep,
                }}>
                    {/* last file op — wide enough for "ci3_1_1234567890.fbot cloud" */}
                    <div style={{ ...base, width: 300, borderRight: sep, paddingLeft: '2em' }}>
                        {metadata.lastFileOp?.filename ? `${metadata.lastFileOp.filename} ${metadata.lastFileOp.cloud === 'true' ? 'cloud' : 'local'}` : ''}
                    </div>
                    {/* title + serial — title flex-grows, serial pinned right */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', padding: '0 8px', overflow: 'hidden', borderRight: sep }}>
                        <span style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{metadata.title || ''}</span>
                        {metadata.serial && <span style={{ marginLeft: 6, flexShrink: 0, fontFamily: 'monospace', color: theme.colors.info }}>{metadata.serial}</span>}
                        {showFingerprintUpdate && (
                            <span
                                title={`New fingerprints available: ser_${updateNotification.fingerprints.latestSerial}.fbot`}
                                onClick={() => {
                                    const base = (updateNotification.fingerprints.latestFilename || '').split('/').pop();
                                    setLoadDialogPreselect({ filename: base, directory: 'library/dev' });
                                    setOpenLoadDialog(true);
                                }}
                                style={{ marginLeft: 5, cursor: 'pointer', color: '#f59e0b', flexShrink: 0 }}
                            >
                                <FontAwesomeIcon icon={faCircleArrowUp} />
                            </span>
                        )}
                    </div>
                    {/* current merge — shows if merge is active */}
                    {currentMerge && (
                        <div style={{ ...base, minWidth: 200, maxWidth: 300, borderRight: sep }}>
                            Merge: {currentMerge.name || 'unknown'}
                        </div>
                    )}
                    {/* branch + short hash — wide enough for "feature/long-name abc1234" */}
                    <div style={{ ...base, width: 150, borderRight: sep }}>
                        {metadata.branch || ''}{metadata.git_hash ? ` ${String(metadata.git_hash).substring(0, 7)}` : ''}
                    </div>
                    {/* connected — wide enough for "Disconnected" */}
                    <div style={{ ...base, width: 100, borderRight: sep }}>
                        {ws ? 'Connected' : 'Disconnected'}
                    </div>
                    <div
                        role="button"
                        tabIndex={0}
                        title={soundsMuted ? 'Unmute sounds' : 'Mute sounds'}
                        onClick={() => {
                            void setMutedPersisted(!soundsMuted);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                void setMutedPersisted(!soundsMuted);
                            }
                        }}
                        style={{ ...base, width: 34, borderRight: sep, cursor: 'pointer', textAlign: 'center', color: soundsMuted ? theme.colors.textMuted : theme.colors.info }}
                    >
                        <FontAwesomeIcon icon={soundsMuted ? faVolumeXmark : faVolumeHigh} />
                    </div>
                    {/* AI total cost — live from token summary updates */}
                    <div
                        style={{ ...base, width: 130, borderRight: sep, fontFamily: 'monospace', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}
                        title="AI total cost (live) and incremental cost"
                    >
                        <span
                            role="button"
                            tabIndex={0}
                            title="Incremental AI cost. Click to clear and reset baseline."
                            onClick={() => { void handleClearIncrementalCost(); }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    void handleClearIncrementalCost();
                                }
                            }}
                            style={{ color: '#60a5fa', cursor: 'pointer', fontVariantNumeric: 'tabular-nums' }}
                        >
                            ${incrementalCost.toFixed(2)}
                        </span>
                        <span style={{ color: theme.colors.text, fontVariantNumeric: 'tabular-nums' }}>
                            ${totalCost.toFixed(2)}
                        </span>
                    </div>
                    {/* dev/ops mode tags — only rendered when active, title cell absorbs the space */}
                    {metadata.devMode && (
                        <div
                            role="button"
                            tabIndex={0}
                            title="Open Dev tools"
                            onClick={() => navigate('/dev')}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    navigate('/dev');
                                }
                            }}
                            style={{ ...base, width: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: sep, cursor: 'pointer' }}
                        >
                            <span style={shimmerDev}>Dev</span>
                        </div>
                    )}
                    {metadata.opsMode && (
                        <div
                            role="button"
                            tabIndex={0}
                            title="Open Ops tools"
                            onClick={() => navigate('/ops')}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    navigate('/ops');
                                }
                            }}
                            style={{ ...base, width: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: sep, cursor: 'pointer' }}
                        >
                            <span style={shimmerOps}>Ops</span>
                        </div>
                    )}
                    {/* username + role — wide enough for "ericv@ipivot.ai (admin)" */}
                    <div style={{ ...base, width: 190, borderRight: sep }}>
                        {authState.username} (
                        <span style={authState.role === 'admin' ? shimmerAdmin : undefined}>{authState.role}</span>
                        )
                    </div>
                    {/* version — wide enough for "3.5.1 / 1019 (fw 1020) / 8 (supported 9)" */}
                    <div style={{ ...base, width: 260 }}>
                        {metadata.appVersion || 'unknown'}
                        {showFirmwareUpdate && (
                            <span
                                title="New firmware available"
                                onClick={() => navigate('/settings/updates?checkNow=1')}
                                style={{ marginLeft: 4, cursor: 'pointer', color: '#f59e0b' }}
                            >
                                <FontAwesomeIcon icon={faCircleArrowUp} />
                            </span>
                        )}
                        {' '}/ {' '}
                        <span style={isFutureDbVersion ? { backgroundColor: '#dc3545', color: '#fff', padding: '0 4px', borderRadius: 3 } : undefined}>
                            {metadata.schemaVersion || 'unknown'}
                        </span>
                        {metadata.supportedSchemaVersion != metadata.schemaVersion && (
                            <span style={{ marginLeft: 4, color: theme.colors.textMuted }}>(fw {metadata.supportedSchemaVersion})</span>
                        )}
                        <span style={{ marginLeft: 4, color: theme.colors.textMuted }}>
                            / {metadata.localSchemaVersion || 'unknown'}
                            {metadata.supportedLocalSchemaVersion != metadata.localSchemaVersion ? ` (supported ${metadata.supportedLocalSchemaVersion})` : ''}
                        </span>
                    </div>
                </div>
                );
            })()}
            <SaveDialog
                isOpen={openSaveDialog}
                filenameTemplate="ci3_1_{serial}.fbot"
                onSave={async (filename, cloud, metadataOverride, progressId) => {
                    try {
                        await exportBackup(filename, cloud, metadataOverride, progressId);
                        setOpenSaveDialog(false);
                        statusContext.postInfo(`Backup saved as ${filename} ${cloud ? 'to cloud' : 'locally'}`);
                        setMetadata(prev => ({
                            ...prev,
                            lastFileOp: {
                                filename: filename,
                                cloud: cloud ? 'true' : 'false'
                            }
                        }));
                    } catch (err) {
                        statusContext.postError(`Failed to save backup: ${err.message}`);
                        throw err;
                    }
                }}
                onCancel={() => {
                    setOpenSaveDialog(false);
                }}
                cloud={true}
            />
            <SaveDialog
                isOpen={openPublishDialog}
                title="Publish"
                primaryLabel="Publish"
                filenameTemplate="ci3_1_{serial}_layer1.fbot"
                onSave={async (filename, cloud, metadataOverride) => {
                    await handlePublishFromMenu(filename, cloud, metadataOverride);
                }}
                onCancel={() => setOpenPublishDialog(false)}
                cloud={true}
            />
            <LoadDialog
                isOpen={openLoadDialog}
                preselectedFilename={loadDialogPreselect?.filename}
                preselectedDirectory={loadDialogPreselect?.directory}
                onLoad={async (filename, cloud, progressId) => {
                    try {
                        const res = await importBackup(filename, cloud, progressId);
                        if (res?.status && res.status !== 'success') {
                            throw new Error(res.message || 'Backup import failed');
                        }
                        console.log('Backup loaded, refreshing settings');
                        statusContext.postInfo(`Backup loaded from ${filename} ${cloud ? 'from cloud' : 'locally'}`);
                        setMetadata(prev => ({
                            ...prev,
                            lastFileOp: {
                                filename: filename,
                                cloud: cloud ? 'true' : 'false'
                            }
                        }));
                        setOpenLoadDialog(false);
                        // The backend replaces the active DB file; force a full client reload
                        // so all pages, caches, and websocket-driven state are rehydrated.
                        window.location.reload();
                    } catch (err) {
                        statusContext.postError(`Failed to load backup: ${err.message}`);
                        throw err;
                    }
                }}
                onCancel={() => {
                    setOpenLoadDialog(false);
                    setLoadDialogPreselect(null);
                }}
                cloud={true}
            />
            <LoadDialog
                isOpen={openMergeLoadDialog}
                title="Merge"
                primaryLabel="Merge"
                fileFilter={/\.merge$/}
                defaultDirectoryChoice="library/queue"
                onLoad={async (filename, cloud) => {
                    try {
                        await mergeLoadFile(cloud === true || cloud === 'true', filename, false);
                        navigate('/data/merge-op');
                    } catch (e) {
                        statusContext.postError(e.message, 5000);
                    }
                    setOpenMergeLoadDialog(false);
                }}
                onCancel={() => {
                    setOpenMergeLoadDialog(false);
                }}
                cloud={true}
                enableProgress={false}
            />
                        </MetadataContext.Provider>
                </StatusContext.Provider>
            </WsContext.Provider>
            </BeContext.Provider>
        </Container>
    </div>
    );
}

export default App;